import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import useStyles from "./style";
import MplusApi from "Services/Mplus";
import { VEHICLE_ACTIONS, getErrorMessage, locationName, missingLabel } from "./constants";

const VehicleActionDialog = ({ action, vehicle, locations, onClose, onDone, notify }) => {
  const classes = useStyles();
  const config = VEHICLE_ACTIONS[action] || {};
  const [locationId, setLocationId] = useState("");
  const [reason, setReason] = useState("");
  const [force, setForce] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setLocationId("");
    setReason("");
    setForce(false);
  }, [action, vehicle]);

  if (!action || !vehicle) return null;

  const missing = vehicle.readiness?.missing || [];
  const isMakeAvailable = action === "make-available";
  const reasonRequired = config.reason === "required" || (isMakeAvailable && force);
  const showReason = Boolean(config.reason) || (isMakeAvailable && force);
  const currentLocationId = vehicle.location?.id || vehicle.location_id;
  const locationOptions = locations.filter((location) => location.id !== currentLocationId || action !== "transfer");

  const handleSubmit = async (event) => {
    event.preventDefault();
    const payload = {};
    if (config.location) payload[config.location] = locationId;
    if (reason.trim()) payload.reason = reason.trim();
    if (isMakeAvailable && force) payload.force = true;

    setSaving(true);
    try {
      await MplusApi.runVehicleAction(vehicle.id, action, payload);
      notify(config.success, "success");
      onDone();
    } catch (error) {
      notify(getErrorMessage(error, "L'action a échoué."), "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="xs" className={classes.entryDialog}>
      <DialogTitle className={classes.entryDialogTitle}>
        <Box>
          <Typography className={classes.dialogTitleText}>{config.label}</Typography>
          <Typography className={classes.dialogSubtitle}>
            {vehicle.product_catalog?.name || "Véhicule"} · {vehicle.vin || vehicle.plate || "-"}
          </Typography>
        </Box>
        <IconButton aria-label="Fermer" onClick={onClose} size="small"><CloseRoundedIcon /></IconButton>
      </DialogTitle>
      <Box component="form" onSubmit={handleSubmit}>
        <DialogContent className={classes.entryDialogContent}>
          <Box className={classes.entryForm}>
            <Typography className={classes.actionDescription}>{config.description}</Typography>
            {isMakeAvailable && missing.length ? (
              <Box className={classes.missingBox}>
                <Typography className={classes.missingTitle}>Contrôles manquants</Typography>
                <ul>{missing.map((item) => <li key={missingLabel(item)}>{missingLabel(item)}</li>)}</ul>
                <FormControlLabel
                  control={<Checkbox size="small" checked={force} onChange={(event) => setForce(event.target.checked)} />}
                  label="Forcer la mise en vente (admin, dérogation tracée)"
                />
              </Box>
            ) : null}
            {config.location ? (
              <Box className={classes.fieldGroup}>
                <Typography className={classes.fieldLabel}>{config.locationLabel} <span>*</span></Typography>
                <TextField select required fullWidth size="small" value={locationId} SelectProps={{ displayEmpty: true }} onChange={(event) => setLocationId(event.target.value)}>
                  <MenuItem value="" disabled>Sélectionner un emplacement</MenuItem>
                  {locationOptions.map((location) => (
                    <MenuItem key={location.id} value={location.id}>{locationName(location)}{location.city ? ` · ${location.city}` : ""}</MenuItem>
                  ))}
                </TextField>
              </Box>
            ) : null}
            {showReason ? (
              <Box className={classes.fieldGroup}>
                <Typography className={classes.fieldLabel}>Motif {reasonRequired ? <span>*</span> : null}</Typography>
                <TextField required={reasonRequired} fullWidth multiline minRows={2} size="small" value={reason} onChange={(event) => setReason(event.target.value)} />
              </Box>
            ) : null}
          </Box>
        </DialogContent>
        <DialogActions className={classes.entryDialogActions}>
          <Button className={classes.cancelButton} onClick={onClose}>Annuler</Button>
          <Button
            className={config.danger ? classes.dangerButton : classes.saveButton}
            type="submit"
            variant="contained"
            disabled={saving}
          >
            {saving ? "..." : "Confirmer"}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default VehicleActionDialog;
